var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { z } from 'zod';
import { Timeouts } from './framework/Timeouts';
import { TransportError } from './framework/Transport';
const commandResponse = z.object({
    value: z.string().optional(),
    status: z.literal('success').or(z.literal('error')),
    code: z.string().optional(),
    message: z.string().optional(),
});
function isResp(schema, resp) {
    return schema.safeParse(resp).success;
}
export class BoseVideoBar {
    constructor(http, state, log, framework) {
        this.http = http;
        this.state = state;
        this.log = log;
        // other class properties and constructor
        this.authCredentials = '';
        this.cameraInfo = {};
        this.timeouts = new Timeouts();
        this.usedIds = {};
        // controls
        this.busy = {
            get: (tags) => __awaiter(this, void 0, void 0, function* () {
                return this.state.busy.value;
            }),
        };
        this.cameras = {
            get: (key, tags) => __awaiter(this, void 0, void 0, function* () {
                return {
                    name: this.cameraInfo[key].name,
                    hasCameraTracking: true,
                    hasPan: true,
                    hasTilt: true,
                    hasZoom: true,
                    location: 'near',
                };
            }),
            getKey: (config) => {
                if (!('id' in config)) {
                    this.log.error(`invalid camera config: ${JSON.stringify(config)}`);
                    return 'invalid';
                }
                return config.id.toString();
            },
            validate: (config) => __awaiter(this, void 0, void 0, function* () {
                if (!('id' in config))
                    return 'incompatible';
                const driverKey = config.id.toString();
                return this.cameraInfo[driverKey] === undefined ? 'missing' : 'ok';
            }),
        };
        this.cameraMove = {
            set: (key, value, tags) => __awaiter(this, void 0, void 0, function* () {
                const cameraInfo = this.cameraInfo[key];
                if (cameraInfo === undefined) {
                    this.log.error(`cameraMove: unknown key ${JSON.stringify(key)}`);
                    return value;
                }
                this.state.cameraPreset.update(undefined);
                if ('direction' in value)
                    cameraInfo.moveDirection = value.direction;
                if (value.action === 'start') {
                    yield this.sendCameraMove('start', cameraInfo, tags);
                }
                else if (value.action === 'stop') {
                    yield this.sendCameraMove('stop', cameraInfo, tags);
                }
                else if (value.action === 'repeat') {
                    yield this.sendCameraMove('continue', cameraInfo, tags);
                }
                return value;
            }),
        };
        this.cameraPosition = {
            get: (key, tags) => __awaiter(this, void 0, void 0, function* () {
                const cameraInfo = this.cameraInfo[key];
                if (cameraInfo === undefined) {
                    this.log.error(`cameraPosition: unknown key ${JSON.stringify(key)}`);
                    return { pan: 0, tilt: 0, zoom: 0 };
                }
                const panResp = yield this.httpGetJson({ cmd: '7' }, commandResponse, [
                    'get.cameraPosition.pan',
                ]);
                const tiltResp = yield this.httpGetJson({ cmd: '8' }, commandResponse, [
                    'get.cameraPosition.tilt',
                ]);
                const zoomResp = yield this.httpGetJson({ cmd: '6' }, commandResponse, [
                    'get.cameraPosition.zoom',
                ]);
                const panValue = (panResp === null || panResp === void 0 ? void 0 : panResp.value) ? parseInt(panResp === null || panResp === void 0 ? void 0 : panResp.value) : 0;
                const tiltValue = (tiltResp === null || tiltResp === void 0 ? void 0 : tiltResp.value) ? parseInt(tiltResp === null || tiltResp === void 0 ? void 0 : tiltResp.value) : 0;
                const zoomValue = (zoomResp === null || zoomResp === void 0 ? void 0 : zoomResp.value) ? parseInt(zoomResp === null || zoomResp === void 0 ? void 0 : zoomResp.value) : 0;
                return {
                    pan: panValue,
                    tilt: tiltValue,
                    zoom: zoomValue,
                };
            }),
        };
        this.cameraPreset = {
            get: (tags) => __awaiter(this, void 0, void 0, function* () {
                return this.state.cameraPreset.value;
            }),
            set: (presetId, tags) => __awaiter(this, void 0, void 0, function* () {
                var _a, _b, _c;
                if (presetId === undefined)
                    return undefined;
                const preset = this.getPreset(presetId);
                if (preset === undefined) {
                    this.log.error(`preset "${presetId}" not found`);
                    return undefined;
                }
                const cameraInfo = this.cameraInfo[preset.cameraId.toString()];
                if (cameraInfo === undefined) {
                    this.log.error(`camera not found for preset "${presetId}"`);
                    return undefined;
                }
                // Set the 'first' preset PTZ to use the PTZ values from the preset.
                yield this.httpPutJson({
                    cmd: '57',
                    data: `data={"57":"${(_a = preset.pan) !== null && _a !== void 0 ? _a : 0} ${(_b = preset.tilt) !== null && _b !== void 0 ? _b : 0} ${(_c = preset.zoom) !== null && _c !== void 0 ? _c : 0}"}`,
                }, commandResponse, tags);
                // Set the active preset to the 'first' preset.
                yield this.httpPutJson({ cmd: '13', data: `data={"13":"1"}` }, commandResponse, [
                    ...tags,
                    'set.activePreset',
                ]);
                // Run the active preset.
                yield this.httpPostJson({ cmd: '0F', data: `data={"0F":""}` }, commandResponse, [
                    ...tags,
                    'set.recallPreset',
                ]);
                return presetId;
            }),
        };
        this.cameraTracking = {
            get: (key, tags) => __awaiter(this, void 0, void 0, function* () {
                const cameraInfo = this.cameraInfo[key];
                if (cameraInfo === undefined) {
                    this.log.error(`cameraTracking: unknown key ${JSON.stringify(key)}`);
                    return this.state.cameraTracking.values[key];
                }
                const resp = yield this.httpGetJson({ cmd: '19' }, commandResponse, tags);
                return 'value' in resp ? resp.value === '1' : this.state.cameraTracking.values[key];
            }),
            set: (key, value, tags) => __awaiter(this, void 0, void 0, function* () {
                const cameraInfo = this.cameraInfo[key];
                if (cameraInfo === undefined) {
                    this.log.error(`cameraTracking: unknown key ${JSON.stringify(key)}`);
                    return value;
                }
                const resp = yield this.httpPutJson({ cmd: '19', data: `data={"19":"${value ? '1' : '0'}"}` }, commandResponse, tags);
                return resp.status === 'success' ? value : this.state.cameraTracking.values[key];
            }),
        };
        this.firmwareVersion = {
            get: (tags) => __awaiter(this, void 0, void 0, function* () {
                var _d;
                const resp = yield this.httpGetJson({ cmd: '16' }, commandResponse, tags);
                return (_d = resp === null || resp === void 0 ? void 0 : resp.value) !== null && _d !== void 0 ? _d : this.state.firmwareVersion.value;
            }),
        };
        this.mute = {
            get: (tags) => __awaiter(this, void 0, void 0, function* () {
                const resp = yield this.httpGetJson({ cmd: '2' }, commandResponse, tags);
                return 'value' in resp ? resp.value === '1' : this.state.mute.value;
            }),
            set: (mute, tags) => __awaiter(this, void 0, void 0, function* () {
                const resp = yield this.httpPutJson({ cmd: '2', data: `data={"2":"${mute ? '1' : '0'}"}` }, commandResponse, tags);
                return resp.status === 'success' ? mute : this.state.mute.value;
            }),
        };
        this.volume = {
            get: (tags) => __awaiter(this, void 0, void 0, function* () {
                var _e;
                const resp = yield this.httpGetJson({ cmd: '3' }, commandResponse, tags);
                const value = (_e = resp.value) !== null && _e !== void 0 ? _e : '0';
                return resp.status === 'success' ? parseInt(value, 10) : this.state.volume.value;
            }),
            set: (volume, tags) => __awaiter(this, void 0, void 0, function* () {
                const resp = yield this.httpPutJson({ cmd: '3', data: `data={"3":"${volume}"}` }, commandResponse, tags);
                return resp.status === 'success' ? volume : this.state.volume.value;
            }),
            range: { max: 100, min: 0, round: 1 },
        };
        // polling
        this.polls = [
            { intervalMs: 3000, controls: ['mute', 'cameraTracking', 'volume'] },
        ];
    }
    // phase callbacks
    onConfigure(config) {
        var _a;
        return __awaiter(this, void 0, void 0, function* () {
            this.cameraInfo = {};
            this.timeouts.clearAll();
            for (const cameraConfig of Object.values((_a = config.cameras) !== null && _a !== void 0 ? _a : {})) {
                if (!('id' in cameraConfig)) {
                    this.log.error(`invalid camera config: ${JSON.stringify(cameraConfig)}`);
                    continue;
                }
                const driverKey = cameraConfig.id.toString();
                this.cameraInfo[driverKey] = Object.assign(Object.assign({}, cameraConfig), { hasPower: true });
            }
        });
    }
    onDisconnect() {
        return __awaiter(this, void 0, void 0, function* () {
            this.timeouts.clearAll();
        });
    }
    onInitialize(credentials, config) {
        var _a, _b;
        return __awaiter(this, void 0, void 0, function* () {
            const username = (_a = credentials.username) !== null && _a !== void 0 ? _a : 'bose';
            const password = (_b = credentials.password) !== null && _b !== void 0 ? _b : 'bose';
            this.authCredentials = btoa(`${username}:${password}`);
            // Check for the product name to cofirm authentication.
            try {
                const resp = yield this.httpGetJson({ cmd: `D6` }, commandResponse, ['initialize']);
                if (resp.status === 'success')
                    return 'connected';
            }
            catch (error) {
                if (error instanceof TransportError && error.code === 'commandFailed') {
                    return 'authFailed';
                }
                const errorString = JSON.stringify(error);
                if (errorString.includes('ECONNREFUSED')) {
                    return 'connectFailed';
                }
            }
            return 'verifyFailed';
        });
    }
    // other class methods
    getPreset(presetId) {
        var _a;
        for (const [configId, cameraInfo] of Object.entries(this.cameraInfo)) {
            const preset = (_a = cameraInfo.presets) === null || _a === void 0 ? void 0 : _a[presetId];
            if (preset !== undefined) {
                return Object.assign(Object.assign({}, preset), { configId, presetId, cameraId: cameraInfo.id });
            }
        }
        return undefined;
    }
    httpGetJson(cmd, schema, tags) {
        return __awaiter(this, void 0, void 0, function* () {
            const requestOptions = {};
            requestOptions.headers = { Authorization: `Basic ${this.authCredentials}` };
            requestOptions.serializer = 'utf8';
            if ('data' in cmd)
                requestOptions.data = cmd.data;
            requestOptions.tags = tags;
            const resp = yield this.http.requestJson('get', `/api?query=${cmd.cmd}`, requestOptions);
            if (!(`${cmd.cmd}` in resp) || !isResp(schema, resp[cmd.cmd])) {
                throw new Error(`unexpected response: ${JSON.stringify(resp)}`);
            }
            return resp[cmd.cmd];
        });
    }
    httpPostJson(cmd, schema, tags) {
        return __awaiter(this, void 0, void 0, function* () {
            const requestOptions = {};
            requestOptions.headers = { Authorization: `Basic ${this.authCredentials}` };
            if ('data' in cmd)
                requestOptions.data = cmd.data;
            requestOptions.tags = tags;
            requestOptions.serializer = 'utf8';
            const resp = yield this.http.requestJson('post', `/api`, requestOptions);
            if (!(`${cmd.cmd}` in resp) || !isResp(schema, resp[cmd.cmd])) {
                throw new Error(`unexpected response: ${JSON.stringify(resp)}`);
            }
            return resp[cmd.cmd];
        });
    }
    httpPutJson(cmd, schema, tags) {
        return __awaiter(this, void 0, void 0, function* () {
            const requestOptions = {};
            requestOptions.headers = {
                Authorization: `Basic ${this.authCredentials}`,
                'Content-Type': 'application/x-www-form-urlencoded',
            };
            if ('data' in cmd)
                requestOptions.data = cmd.data;
            requestOptions.tags = tags;
            requestOptions.serializer = 'utf8';
            const resp = yield this.http.requestJson('put', `/api`, requestOptions);
            if (!(`${cmd.cmd}` in resp) || !isResp(schema, resp[cmd.cmd])) {
                throw new Error(`unexpected response: ${JSON.stringify(resp)}`);
            }
            return resp;
        });
    }
    sendCameraMove(action, cameraInfo, tags) {
        return __awaiter(this, void 0, void 0, function* () {
            let cmd = '';
            const direction = cameraInfo.moveDirection === 'left'
                ? cameraInfo.invertPan
                    ? 'right'
                    : 'left'
                : cameraInfo.moveDirection === 'right'
                    ? cameraInfo.invertPan
                        ? 'left'
                        : 'right'
                    : cameraInfo.moveDirection;
            if (action === 'start' || action === 'continue') {
                if (direction === 'left') {
                    cmd = '0B';
                }
                else if (direction === 'right') {
                    cmd = '0C';
                }
                else if (direction === 'down') {
                    cmd = '0E';
                }
                else if (direction === 'up') {
                    cmd = '0D';
                }
                else if (direction === 'out') {
                    cmd = '0A';
                }
                else if (direction === 'in') {
                    cmd = '9';
                }
                else {
                    this.log.error(`invalid move direction: ${cameraInfo.moveDirection}`);
                    return;
                }
            }
            else if (action === 'stop') {
                // nop
            }
            if (cmd !== '') {
                yield this.httpPostJson({ cmd: cmd, data: `data={"${cmd}":""}` }, commandResponse, tags);
            }
        });
    }
}
// static properties
BoseVideoBar.driverType = 'httpDriver';
BoseVideoBar.key = '-Ng-zCfhKcYaq0xdoMH_';
BoseVideoBar.httpRequestOptions = {
    prettyCommands: false,
    scheme: 'https',
    idleTimeoutMs: 20000,
};
